import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Loader2, Plus } from "lucide-react";
import { useState } from "react";

import type {
	AttachRdoPhotoInput,
	RdoActivityInput,
	RdoPhotoSize,
} from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { Stage } from "@/features/stages/types.ts";
import type { Rdo, RdoActivity } from "../../types.ts";
import { combineReportDateTime } from "../../utils/format.ts";
import {
	isRdoPhotoSize,
	RDO_PHOTO_SIZE_LABELS,
	RDO_PHOTO_SIZES,
} from "../../utils/photo-size.ts";
import { RdoActivityCard } from "../rdo-activity-card";

interface ActivitiesSectionProps {
	rdo: Rdo;
	constructionId: string;
	stages: Stage[];
	readOnly: boolean;
	creating: boolean;
	onCreate: (input: RdoActivityInput) => Promise<unknown>;
	onUpdate: (activityId: string, input: RdoActivityInput) => void;
	onDelete: (activity: RdoActivity) => void;
	onReorder: (ids: string[]) => void;
	onAttachPhotos: (
		activityId: string,
		inputs: AttachRdoPhotoInput[],
	) => Promise<unknown>;
	onUpdateCaption: (photoId: string, caption: string) => void;
	onDetachPhoto: (photoId: string) => void;
	onPhotoSizeChange: (photoSize: RdoPhotoSize) => void;
}

function defaultOccurredAt(reportDate: string): string {
	const now = new Date();
	const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
	return combineReportDateTime(reportDate, time) ?? now.toISOString();
}

export function ActivitiesSection({
	rdo,
	constructionId,
	stages,
	readOnly,
	creating,
	onCreate,
	onUpdate,
	onDelete,
	onReorder,
	onAttachPhotos,
	onUpdateCaption,
	onDetachPhoto,
	onPhotoSizeChange,
}: ActivitiesSectionProps) {
	const [newTitle, setNewTitle] = useState("");
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);
	const ids = rdo.activities.map((activity) => activity.id);

	function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const from = ids.indexOf(String(active.id));
		const to = ids.indexOf(String(over.id));
		if (from < 0 || to < 0) return;
		onReorder(arrayMove(ids, from, to));
	}

	async function submitNew(event: React.FormEvent) {
		event.preventDefault();
		const title = newTitle.trim();
		if (!title) return;
		await onCreate({
			title,
			occurredAt: defaultOccurredAt(rdo.reportDate),
			description: "",
		});
		setNewTitle("");
	}

	return (
		<section className="rdo-editor__section" aria-labelledby="rdo-activities">
			<div className="rdo-editor__section-header">
				<h2 id="rdo-activities" className="rdo-editor__section-title">
					Atividades
				</h2>
				<span className="rdo-editor__section-count">
					{rdo.activities.length}
				</span>
				<div className="rdo-editor__photo-size">
					<Label htmlFor="rdo-photo-size">Tamanho das fotos no PDF</Label>
					<Select
						value={rdo.photoSize}
						disabled={readOnly}
						onValueChange={(next) => {
							if (isRdoPhotoSize(next)) onPhotoSizeChange(next);
						}}
					>
						<SelectTrigger
							id="rdo-photo-size"
							className="rdo-editor__photo-size-trigger"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{RDO_PHOTO_SIZES.map((size) => (
								<SelectItem key={size} value={size}>
									{RDO_PHOTO_SIZE_LABELS[size]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{rdo.activities.length === 0 ? (
				<p className="rdo-editor__empty">
					{readOnly
						? "Nenhuma atividade registrada."
						: "Nenhuma atividade ainda. Adicione a primeira abaixo."}
				</p>
			) : (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext items={ids} strategy={verticalListSortingStrategy}>
						<div className="rdo-editor__activities">
							{rdo.activities.map((activity, index) => (
								<RdoActivityCard
									key={activity.id}
									activity={activity}
									index={index}
									constructionId={constructionId}
									reportDate={rdo.reportDate}
									stages={stages}
									readOnly={readOnly}
									onUpdate={(input) => onUpdate(activity.id, input)}
									onDelete={() => onDelete(activity)}
									onAttachPhotos={(inputs) => onAttachPhotos(activity.id, inputs)}
									onUpdateCaption={onUpdateCaption}
									onDetachPhoto={onDetachPhoto}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
			)}

			{!readOnly && (
				<form className="rdo-editor__new-activity" onSubmit={submitNew}>
					<Input
						aria-label="Título da nova atividade"
						placeholder="Título da nova atividade"
						value={newTitle}
						maxLength={200}
						onChange={(event) => setNewTitle(event.target.value)}
						disabled={creating}
					/>
					<Button type="submit" disabled={creating || !newTitle.trim()}>
						{creating ? <Loader2 /> : <Plus />}
						Adicionar atividade
					</Button>
				</form>
			)}
		</section>
	);
}
